import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import ActionButtons from '../../crm/shared/field_inputs/shared/ActionButtons';
import { DataContext as SharedDataContext } from '../../../stores/crm-store';
import OnHoverTooltip from '../../tables/shared/OnHoverTooltip';
import ExlMailCalendarIcons from '../icons/ExlMailCalendarIcons';
import { useTranslation } from 'react-i18next';
import { GENERAL_ERROR_MESSAGE, GENERAL_ERROR_CONTACT_MESSAGE, COURSE_TEMPLATE_TOOLTIP } from '../../../i18n/keyConstants';

const ExlCourseFormModal = ({
    label,
    description,
    defaultFormValues,
    defaultFormComponents,
    handleAdd,
    onSuccessiveSave,
    onValueSet,
    buttonType,
    disabled = false,
    disabledTooltipText,
    callback,
    handleCancel,
    trueText,
    falseText,
    tooltipPlace,
    mailCalendarIconsEnabled = true
}) => {
    const { t } = useTranslation(['errorMessages', 'shared']);

    buttonType = buttonType ? buttonType : 'btn-success';

    // ------------------------ States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formValues, setFormValues] = useState(defaultFormValues ??{});
    const [formComponents, setFormComponents] = useState(defaultFormComponents);
    const [errorMessages, setErrorMessages] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    const setFormValue = (propName, value) => {
        const newValues = { ...formValues };
        newValues[propName] = value;
        formComponents.forEach((c) => {
            if (!c.bindOnChange) return;
            const boundChange = c.bindOnChange(newValues, propName);
            if (boundChange) {
                newValues[c.propName] = boundChange;
            }
        });

        if (onValueSet) {
            onValueSet(propName, newValues[propName]);
        }
        setFormValues(newValues);
    };
    const setErrorMessage = (propName, e) => {
        errorMessages[propName] = e;
        setErrorMessages({ ...errorMessages });
    };

    const checkWindowSize = () => {
        setIsMobile(window.innerWidth < 575);
        setIsTablet(window.innerWidth > 760 && window.innerWidth < 1024)
    }

    useEffect(() => {
        setFormValues(defaultFormValues ?? {});
        setFormComponents(defaultFormComponents);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFormValues]);

    useEffect(() => {
        if (callback) {
            setFormComponents(callback(formValues, defaultFormComponents))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);


    const { store, dispatch } = useContext(SharedDataContext);

    // ------------------------ Methods
    const renderModalFormComponent = (component, formValues, setFormValue, errorMessages) => {
        const errorMessage = errorMessages[component.propName];
        const isInvalid = errorMessage && errorMessage.length;

        return (
            <div key={component.propName}>
                {component.visible !== false ?
                    <div>
                        {component.label ? <label>{component.label}</label> : <div style={{ paddingTop: '.3rem' }} />}
                        {component.label === "Kursmall" || component.label === "Course template"
                            ?
                            <OnHoverTooltip dataFor="course-template" tooltipPlace="right" tooltipEffect="solid">
                                <span className='oi oi-info c-steelblue padding-left-10px' style={{ fontSize: "10px" }} />
                                {t(COURSE_TEMPLATE_TOOLTIP, { ns: 'shared' })}
                            </OnHoverTooltip>
                            :
                            ""}
                        {React.cloneElement(component.component, {
                            isInvalid,
                            value: formValues[component.propName],
                            setValue: (v) => setFormValue(component.propName, v),
                        })}
                        {isInvalid && <div className='crm-invalid-msg'>{errorMessage}</div>}
                </div> : null }
            </div>
        );
    };

    const clearForm = () => {
        setFormValues({ ...defaultFormValues });
        setErrorMessages({});
    };

    const onSave = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const item = { ...formValues };

        Object.keys(item).forEach((propName) => {
            if (item[propName]?.id !== undefined) {
                item[propName + 'Id'] = item[propName].id;
            }
        });

        let isInvalid = false;
        formComponents.forEach((c) => {
            if (c.validate) {
                const msg = c.validate(item[c.propName], item);
                setErrorMessage(c.propName, msg);
                if (msg && msg.length) {
                    isInvalid = true;
                }
            }
        });

        if (isInvalid) {
            setIsSaving(false);
            return;
        }

        const response = await handleAdd(item);
        setIsSaving(false);
        if (response.success) {
            await new Promise((r) => setTimeout(r, 200));
            clearForm();
            setIsModalOpen(false);
            dispatch({ type: 'SHOW_SAVED_TOAST' });

            if (onSuccessiveSave) {
                onSuccessiveSave(response);
            }
        } else {
            dispatch({ type: 'SHOW_ERROR_TOAST' });
            alert(`${t(GENERAL_ERROR_MESSAGE)}:\n${response.error ?? response.message ?? t(GENERAL_ERROR_CONTACT_MESSAGE)}`);
        }
    };

    const onCancel = () => {
        clearForm();
        if (typeof handleCancel === 'function') {
            handleCancel();
        }
        setIsModalOpen(false);
    };

    const toggleButton = (
        <button
            type='button'
            disabled={disabled}
            className={`btn ${buttonType} mx-1 crm-modal-btn justify-content-center align-content-center`}
            onClick={() => setIsModalOpen(true)}>
            <div>{label}</div>
        </button>
    );

    return (
        <>
            {disabledTooltipText ? (
                <OnHoverTooltip
                    labelClassNames='margin-none'
                    dataFor='show-won-button'
                    tooltipType='dark'
                    tooltipPlace='left'
                    tooltipEffect='solid'
                    style={{ width: 100 }}                >
                    {toggleButton}
                    <span className='d-flex my-auto'>{disabledTooltipText}</span>
                </OnHoverTooltip>
            ) : (
                toggleButton
            )}
            {!isMobile ? (
                <Modal show={isModalOpen} onHide={onCancel} className={'exl-modal'}>
                    <div className='d-flex justify-content-between align-items-center padding-top-20px padding-left-20px padding-right-20px'>
                        <h4 className='margin-none'>{description}</h4>
                        <button type='button' className='btn btn-secondary crm-modal-btn ' onClick={onCancel}>
                            &times;
                        </button>
                    </div>
                    <div className='add-item-form container'>
                        <Modal.Body className="container">
                            <div className="row">
                                <div className="col-3 field-input-column">
                                    {formComponents && formComponents.filter(function (c) {
                                        if (c.propName === 'description')
                                            return false;
                                        return true;
                                    }).map((c) => {
                                        return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                                    })}
                                </div>
                                <div className={isTablet ? "col-7 desc-input-column" : "col-9 desc-input-column"}>
                                    {formComponents && formComponents.filter(function (c) {
                                        if (c.propName !== 'description')
                                            return false;
                                        return true;
                                    }).map((c) => {
                                        return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                                    })}
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="exl-modal-footer"  >
                            {mailCalendarIconsEnabled ?
                                formValues?.sendEmailNotifications !== undefined ? (
                                    <ExlMailCalendarIcons
                                        sendEmailNotifications={formValues.sendEmailNotifications}
                                        trueText={trueText}
                                        falseText={falseText}
                                        tooltipPlace={tooltipPlace}
                                    />
                                ) : formValues?.receiveEmailNotifications !== undefined ? (
                                    <ExlMailCalendarIcons
                                        sendEmailNotifications={formValues.receiveEmailNotifications}
                                        trueText={trueText}
                                        falseText={falseText}
                                        tooltipPlace={tooltipPlace}
                                    />
                                ) : (
                                    <ExlMailCalendarIcons
                                        sendEmailNotifications={false}
                                        falseText={falseText}
                                        tooltipPlace={tooltipPlace}
                                    />
                                ) :
                                <></>}
                            <ActionButtons {...{ onCancel, onSave, isSaving }} />
                        </Modal.Footer>
                    </div>
                </Modal>
            ) : (
                <Modal show={isModalOpen} onHide={onCancel} className={'exl-modal'}>
                    <div className='d-flex justify-content-between align-items-center padding-top-20px padding-left-20px padding-right-20px'>
                        <h4 className='margin-none'>{description}</h4>
                        <button type='button' className='btn btn-secondary crm-modal-btn ' onClick={onCancel}>
                            &times;
                        </button>
                    </div>
                    <div className='add-item-form container'>
                        <Modal.Body className="container">
                            <div className="row">
                                <div className="col-9 field-input-column">
                                    {formComponents && formComponents.map((c) => {
                                        return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                                    })}
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="exl-modal-footer">
                            {formValues?.sendEmailNotifications !== undefined ? (
                                <ExlMailCalendarIcons
                                    sendEmailNotifications={formValues.sendEmailNotifications}
                                    trueText={trueText}
                                    falseText={falseText}
                                    tooltipPlace={tooltipPlace}
                                />
                            ) : formValues?.receiveEmailNotifications !== undefined ? (
                                <ExlMailCalendarIcons
                                    sendEmailNotifications={formValues.receiveEmailNotifications}
                                    trueText={trueText}
                                    falseText={falseText}
                                    tooltipPlace={tooltipPlace}
                                />
                            ) : (
                                <ExlMailCalendarIcons
                                    sendEmailNotifications={false}
                                    falseText={falseText}
                                    tooltipPlace={tooltipPlace}
                                />
                            )}
                            <ActionButtons {...{ onCancel, onSave, isSaving }} />
                        </Modal.Footer>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ExlCourseFormModal;
